import { useCallback, useMemo, useState } from "react";
import { useTheme } from "../app/context/ThemeContext";
import { usePins } from "../app/context/PinsContext";
import { USERNAME_MAX_LEN } from "../app/state/appUtils";
import { peerAvatarImageSources, avatarTerminalTintStyle } from "../lib/avatarDisplay";
import { AvatarImgWithVariantFallback } from "./AvatarImgWithVariantFallback";
import { useLongPress, type LongPressCoords } from "../hooks/useLongPress";
import type { ContactRow } from "../lib/db";
import type { PresenceStatus } from "../styles/theme";
import type { VisualMode } from "../styles/themePalette";
import { AppIcon } from "./AppIcon";
import { ContactRequest } from "./ContactRequest";
import { SidebarContextMenu } from "./SidebarContextMenu";
import { UserPlusIcon } from "./UserPlusIcon";

type IncomingReq = {
  from_id: string;
  username: string;
};

type ContactListProps = {
  /** Вставка перед нижним блоком (напр. VAULTS) — только десктоп. */
  sidebarBeforeFooter?: React.ReactNode;
  contacts: ContactRow[];
  incoming: IncomingReq[];
  selectedPeerId: string | null;
  unreadCounts: Record<string, number>;
  presenceByUserId: Record<string, PresenceStatus>;
  addUser: string;
  isMobile: boolean;
  /** When true, content is wrapped by parent `.sidebar-inner--scroll-root` (mobile). */
  embedded?: boolean;
  channelsCollapsible?: boolean;
  channelsExpanded?: boolean;
  onToggleChannels?: () => void;
  onAddUserChange: (value: string) => void;
  onSendRequest: () => void;
  onAcceptRequest: (fromId: string) => void;
  onSelectPeer: (peerId: string) => void;
};

export function ContactList({
  sidebarBeforeFooter,
  contacts,
  incoming,
  selectedPeerId,
  unreadCounts,
  presenceByUserId,
  addUser,
  isMobile,
  embedded = false,
  channelsCollapsible = false,
  channelsExpanded = true,
  onToggleChannels,
  onAddUserChange,
  onSendRequest,
  onAcceptRequest,
  onSelectPeer,
}: ContactListProps) {
  const body = (
    <ContactListBody
      sidebarBeforeFooter={sidebarBeforeFooter}
      contacts={contacts}
      incoming={incoming}
      selectedPeerId={selectedPeerId}
      unreadCounts={unreadCounts}
      presenceByUserId={presenceByUserId}
      addUser={addUser}
      isMobile={isMobile}
      channelsCollapsible={channelsCollapsible}
      channelsExpanded={channelsExpanded}
      onToggleChannels={onToggleChannels}
      onAddUserChange={onAddUserChange}
      onSendRequest={onSendRequest}
      onAcceptRequest={onAcceptRequest}
      onSelectPeer={onSelectPeer}
    />
  );
  if (embedded) return body;
  return <div className="sidebar-inner">{body}</div>;
}

function ContactListBody({
  sidebarBeforeFooter,
  contacts,
  incoming,
  selectedPeerId,
  unreadCounts,
  presenceByUserId,
  addUser,
  isMobile: _isMobile,
  channelsCollapsible,
  channelsExpanded,
  onToggleChannels,
  onAddUserChange,
  onSendRequest,
  onAcceptRequest,
  onSelectPeer,
}: Omit<ContactListProps, "embedded">) {
  const { visualMode, preference } = useTheme();
  const { isPinned } = usePins();
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; contactId: string } | null>(null);
  const onLongPressContact = useCallback(
    (contactId: string) => (coords: LongPressCoords) => {
      setCtxMenu({ ...coords, contactId });
    },
    [],
  );

  const dmUnreadTotal = useMemo(
    () => contacts.reduce((sum, c) => sum + (unreadCounts[c.user_id] ?? 0), 0),
    [contacts, unreadCounts],
  );

  const sectionGrow =
    channelsCollapsible ? (channelsExpanded ? "sidebar-section--grow" : "") : "sidebar-section--grow";

  const bodyExpanded = channelsCollapsible ? channelsExpanded : true;

  const titleBlock = channelsCollapsible ? (
    <button
      type="button"
      className="workspace-nav__head-toggle channels-section__head-toggle"
      onClick={onToggleChannels}
      aria-expanded={channelsExpanded}
    >
      <span className="workspace-nav__title channels-section__title" title="// ENCRYPTED CHANNELS">
        // ENCRYPTED CHANNELS
      </span>
      <AppIcon
        name={channelsExpanded ? "chevron-down" : "chevron-up"}
        size={19}
        className="sidebar-section__chevron"
      />
    </button>
  ) : (
    <span className="workspace-nav__title channels-section__title" title="// ENCRYPTED CHANNELS">
      // ENCRYPTED CHANNELS
    </span>
  );

  return (
    <>
      <div className={`sidebar-section ${sectionGrow}`.trim()}>
        <div
          className={`workspace-nav__head sidebar-section__toolbar channels-section__head ${channelsCollapsible ? "workspace-nav__head--collapsible" : ""}`}
        >
          {titleBlock}
          <div className="workspace-nav__head-right sidebar-section__head-actions">
            {channelsCollapsible && !channelsExpanded && dmUnreadTotal > 0 ? (
              <span className="contact-row__badge channels-section__collapsed-badge" aria-label="Непрочитано">
                {dmUnreadTotal}
              </span>
            ) : null}
            <button
              type="button"
              className="sidebar-section__action-btn channels-section__user-plus"
              title="Добавить контакт"
              aria-label="Добавить контакт"
              onClick={(e) => {
                e.stopPropagation();
                setAddUserOpen((v) => !v);
              }}
            >
              <UserPlusIcon className="sidebar-section__action-icon channels-section__user-plus-icon" aria-hidden="true" />
            </button>
          </div>
        </div>

        {addUserOpen && bodyExpanded ? (
          <form
            className="workspace-nav__create-form channels-section__add-form"
            onSubmit={(e) => {
              e.preventDefault();
              onSendRequest();
              setAddUserOpen(false);
            }}
          >
            <input
              value={addUser}
              onChange={(e) => onAddUserChange(e.target.value)}
              placeholder="ник контакта…"
              autoComplete="off"
              maxLength={USERNAME_MAX_LEN}
              className="workspace-nav__create-input"
            />
            <button type="submit" className="workspace-nav__create-ok" disabled={!addUser.trim()}>
              OK
            </button>
          </form>
        ) : null}

        <div className={`sidebar-section__body-wrap ${bodyExpanded ? "is-expanded" : ""}`}>
          <div className="sidebar-section__body-inner">
            {incoming.length > 0 ? (
              <div className="channels-section__requests">
                <div className="channels-section__requests-head">
                  <span className="channels-section__requests-label">Запросы</span>
                  <span className="sidebar-section__meta">{incoming.length}</span>
                </div>
                <div className="request-list">
                  {incoming.map((request) => (
                    <ContactRequest
                      key={request.from_id}
                      username={request.username}
                      onAccept={() => onAcceptRequest(request.from_id)}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {contacts.length === 0 && incoming.length === 0 ? (
              <p className="channels-section__empty">Нет контактов — добавьте по нику.</p>
            ) : (
              <>
                <ul className="contact-list">
                  {contacts.map((contact) => (
                    <DmContactRow
                      key={contact.user_id}
                      contact={contact}
                      isSelected={contact.user_id === selectedPeerId}
                      pinned={isPinned("contact", contact.user_id)}
                      unread={unreadCounts[contact.user_id] ?? 0}
                      presence={presenceByUserId[contact.user_id] ?? "offline"}
                      visualMode={visualMode}
                      palette={preference.palette}
                      onSelect={() => onSelectPeer(contact.user_id)}
                      onLongPress={onLongPressContact(contact.user_id)}
                    />
                  ))}
                </ul>
                {ctxMenu ? (
                  <SidebarContextMenu
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    mode="personal"
                    entityType="contact"
                    entityId={ctxMenu.contactId}
                    onClose={() => setCtxMenu(null)}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {sidebarBeforeFooter}

      <div className="sidebar-footer">
        <div className="sidebar-footer__info">
          <span>
            <em>STXR</em> // X25519 + XSalsa20-Poly1305
          </span>
          <span>
            <em>KEYS</em> // generated locally, never leave device
          </span>
          <span>
            <em>NODE</em> // relay only, zero knowledge
          </span>
        </div>
      </div>
    </>
  );
}

function DmContactRow({
  contact,
  isSelected,
  pinned,
  unread,
  presence,
  visualMode,
  palette,
  onSelect,
  onLongPress,
}: {
  contact: ContactRow;
  isSelected: boolean;
  pinned: boolean;
  unread: number;
  presence: PresenceStatus;
  visualMode: VisualMode;
  palette: { accent: string };
  onSelect: () => void;
  onLongPress: (coords: LongPressCoords) => void;
}) {
  const lp = useLongPress(onLongPress);
  const isOnline = presence === "online" || presence === "typing";
  const initial = contact.username.charAt(0).toUpperCase();
  const { primary: avatarPrimary, fallback: avatarFallback } = peerAvatarImageSources(visualMode, contact);
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        className={`contact-row ${isSelected ? "is-selected" : ""}`}
        onClick={onSelect}
        onKeyDown={(e) => { if (e.key === "Enter") onSelect(); }}
        {...lp}
      >
        <div className={`contact-avatar ${isOnline ? "is-online" : ""}`}>
          <AvatarImgWithVariantFallback
            primary={avatarPrimary}
            fallback={avatarFallback}
            imgClassName={`contact-avatar__img${visualMode === "terminal" ? " contact-avatar__img--terminal" : ""}`}
            style={visualMode === "terminal" ? avatarTerminalTintStyle(palette.accent) : undefined}
            letterClassName="contact-avatar__letter"
            initial={initial}
          />
        </div>
        <div className="contact-row__info">
          <span className={`contact-row__name ${isOnline ? "is-online" : ""}`}>
            {pinned ? <AppIcon name="pin" size={12} className="contact-row__pin-icon" /> : null}
            @{contact.username}
          </span>
        </div>
        <div className="contact-row__tail">
          {presence !== "offline" ? (
            <span className={`presence-dot presence-dot--${presence}`} aria-hidden="true" />
          ) : null}
          {unread > 0 ? <span className="contact-row__badge">{unread}</span> : null}
        </div>
      </div>
    </li>
  );
}
